import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import EmployeeFrame from './EmployeeFrame';
import { Button } from '../../ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { STEPS, stepIndex, stepProgress } from './wizardConfig';
import { useDevice } from '../DeviceContext';

import EmploymentContext from './steps/EmploymentContext';
import WorkingPattern from './steps/WorkingPattern';
import WfhEquipment from './steps/WfhEquipment';
import Lighting from './steps/Lighting';
import Heating from './steps/Heating';
import Cooling from './steps/Cooling';
import RenewableEnergy from './steps/RenewableEnergy';
import Commuting from './steps/Commuting';
import ReviewSubmit from './steps/ReviewSubmit';

const STEP_COMPONENTS = {
  employment: EmploymentContext,
  'working-pattern': WorkingPattern,
  'wfh-equipment': WfhEquipment,
  lighting: Lighting,
  heating: Heating,
  cooling: Cooling,
  renewable: RenewableEnergy,
  commuting: Commuting,
  review: ReviewSubmit,
};

const StepWizard = () => {
  const { slug } = useParams();
  const history = useHistory();
  const { device } = useDevice();
  const i = stepIndex(slug);
  const step = STEPS[i] || STEPS[0];
  const Component = STEP_COMPONENTS[step.slug];

  const goTo = (targetSlug) => history.push(`/prototype/wfh-survey/employee/step/${targetSlug}`);
  const next = () => {
    if (i === STEPS.length - 1) {
      history.push('/prototype/wfh-survey/employee/thank-you');
      return;
    }
    goTo(STEPS[i + 1].slug);
  };
  const back = () => {
    if (i <= 0) {
      history.push('/prototype/wfh-survey/employee/welcome');
      return;
    }
    goTo(STEPS[i - 1].slug);
  };

  const isLast = i === STEPS.length - 1;
  const saveAndLeave = () => alert('Progress saved. You can return via your original email link.');

  // Mobile: full-width primary action on top (thumb-zone friendly), with
  // Back + Save as de-emphasised secondary row underneath flush to the card
  // edges. Tablet/desktop have horizontal room for the traditional 3-button
  // row, but the ghost "Back" is nudged left with -ml-5 so its visual edge
  // aligns with the primary "Next" button on the right.
  const Footer = device === 'mobile' ? (
    <div className='pt-4 mt-4 border-t border-border space-y-3'>
      <Button size='lg' className='w-full' onClick={next}>
        {isLast ? 'Submit' : 'Continue'}
        {!isLast && <ChevronRight size={14} strokeWidth={1.5} />}
      </Button>
      <div className='flex items-center justify-between'>
        <Button variant='ghost' size='sm' className='-ml-3' onClick={back}>
          <ChevronLeft size={12} strokeWidth={1.5} />
          Back
        </Button>
        <Button variant='text' size='sm' className='-mr-1' onClick={saveAndLeave}>
          Save &amp; continue later
        </Button>
      </div>
    </div>
  ) : (
    <div className='flex items-center justify-between gap-3 pt-4 mt-4 border-t border-border'>
      <Button variant='ghost' size='default' className='-ml-5' onClick={back}>
        <ChevronLeft size={14} strokeWidth={1.5} />
        Back
      </Button>
      <Button variant='text' size='sm' onClick={saveAndLeave}>
        Save &amp; continue later
      </Button>
      <Button size='default' onClick={next}>
        {isLast ? 'Submit' : 'Next'}
        {!isLast && <ChevronRight size={14} strokeWidth={1.5} />}
      </Button>
    </div>
  );

  return (
    <EmployeeFrame
      progress={stepProgress(step.slug)}
      step={i + 1}
      totalSteps={STEPS.length}
      currentSlug={step.slug}
      onBack={back}>
      {step.skipWhen ? (
        <div className='mb-3'>
          <span className='text-[10px] text-muted-foreground'>
            hidden when <code className='font-mono'>{step.skipWhen}</code>
          </span>
        </div>
      ) : null}

      <div className='flex-1'>{Component ? <Component /> : <div>Unknown step</div>}</div>

      {Footer}
    </EmployeeFrame>
  );
};

export default StepWizard;
